/**
 * The scribble scene — the vector model behind the drawing pad.
 *
 * WHY A SCENE AND NOT JUST PIXELS: a flattened image has no boxes to move and
 * no arrow to re-point, which is exactly what a workflow diagram needs. The
 * scene is what the user edits; the PNG it renders is what the note carries.
 *
 * WIRE SHAPE IS ERPat'S. The field names here (`t/x/y/w/h/c/f/s/l/p`) and the
 * envelope (`{v, pad, w, h, items}`) are the contract of `notes`.`scribble_data`,
 * which the End-User API has carried since Notes 1.10.0: `GET /v1/api/me/notes/{id}`
 * returns a scene and POST/PATCH accept one. They are deliberately terse and
 * deliberately not renamed — a scene drawn here is the shape ERPat stores, and
 * one drawn on the web pad opens here with its boxes and arrows intact.
 *
 * The validation below is a port of the server's `Scribble_scene::sanitize()`.
 * That class is shared by both of ERPat's own writers precisely so they cannot
 * drift on what a valid drawing is; this file is the third writer, so when that
 * one changes, change this one.
 */

/** Every tool the pad offers, including the two that create nothing. */
export type SketchTool =
  | 'select'
  | 'pen'
  | 'process'
  | 'decision'
  | 'terminator'
  | 'ellipse'
  | 'arrow'
  | 'line'
  | 'text'
  | 'eraser';

/** The tools that actually put an item in the scene. */
export type SketchShape = Exclude<SketchTool, 'select' | 'eraser'>;

export type SketchPaper = 'blank' | 'lined' | 'grid' | 'dotted';

/** One drawn thing. Terse keys are the stored contract — see the file header. */
export interface SketchItem {
  /** Shape kind. */
  t: SketchShape;
  x: number;
  y: number;
  /** Width, or the x-delta of a line/arrow (may be negative until normalised). */
  w: number;
  /** Height, or the y-delta of a line/arrow. */
  h: number;
  /** Ink colour, `#rrggbb`. */
  c: string;
  /** Fill colour, or `''` for none. */
  f: string;
  /** Stroke weight. */
  s: number;
  /** Label (shape) or the content itself (text). */
  l: string;
  /** Freehand path, `pen` only. */
  p?: Array<[number, number]>;
}

export interface SketchScene {
  /** Schema version. ERPat writes and expects 1. */
  v: 1;
  pad: SketchPaper;
  /** Scene-space dimensions the coordinates are relative to. */
  w: number;
  h: number;
  items: SketchItem[];
}

export interface SketchRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

/**
 * Scene-space size. ERPat's pad is 1000x620 and coordinates are stored raw, so
 * matching it is what keeps a scene portable between the two pads rather than
 * merely similar. The canvas is displayed scaled to fit; only the export
 * resolution differs.
 */
export const SCENE_W = 1000;
export const SCENE_H = 620;

/** ERPat's six ink colours, in its order — the first is the default. */
export const SKETCH_COLOURS: ReadonlyArray<{ value: string; label: string }> = [
  { value: '#1f2933', label: 'Ink' },
  { value: '#1d97a6', label: 'Teal' },
  { value: '#2b6cb0', label: 'Blue' },
  { value: '#2f855a', label: 'Green' },
  { value: '#b7791f', label: 'Amber' },
  { value: '#c0392b', label: 'Red' },
];

/** Thin / medium / thick, matching the web pad's three weights. */
export const SKETCH_WEIGHTS = [2, 4, 7] as const;

export const SKETCH_PAPERS: ReadonlyArray<{ value: SketchPaper; label: string }> = [
  { value: 'blank', label: 'Blank' },
  { value: 'lined', label: 'Lined' },
  { value: 'grid', label: 'Grid' },
  { value: 'dotted', label: 'Dotted' },
];

const FONT = '"Inter", "Helvetica Neue", Arial, sans-serif';

export function emptyScene(pad: SketchPaper = 'blank'): SketchScene {
  return { v: 1, pad, w: SCENE_W, h: SCENE_H, items: [] };
}

/** Shapes with an interior — they can be filled and carry a centred label. */
export function isClosed(t: SketchTool): boolean {
  return t === 'process' || t === 'decision' || t === 'terminator' || t === 'ellipse';
}

/** Two-ended shapes — they resize by their endpoints, not a corner. */
export function isSegment(t: SketchTool): boolean {
  return t === 'line' || t === 'arrow';
}

/** A light wash of the ink colour, so a filled box never hides its label. */
export function tint(hex: string): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  if (Number.isNaN(r) || Number.isNaN(g) || Number.isNaN(b)) return '';
  const up = (v: number): string =>
    `0${Math.round(v + (255 - v) * 0.86).toString(16)}`.slice(-2);
  return `#${up(r)}${up(g)}${up(b)}`;
}

/**
 * Give a closed shape a positive width/height, so hit testing and the resize
 * handle never have to reason about a mirrored box. Mutates in place.
 */
export function normalise(item: SketchItem): SketchItem {
  if (item.w < 0) {
    item.x += item.w;
    item.w = -item.w;
  }
  if (item.h < 0) {
    item.y += item.h;
    item.h = -item.h;
  }
  return item;
}

function normalisedBox(item: SketchItem): SketchRect {
  return {
    x: Math.min(item.x, item.x + item.w),
    y: Math.min(item.y, item.y + item.h),
    w: Math.abs(item.w),
    h: Math.abs(item.h),
  };
}

export function bboxOfPoints(points: ReadonlyArray<[number, number]>): SketchRect {
  const first = points[0];
  if (!first) return { x: 0, y: 0, w: 0, h: 0 };
  let minX = first[0];
  let maxX = first[0];
  let minY = first[1];
  let maxY = first[1];
  for (const [px, py] of points) {
    minX = Math.min(minX, px);
    maxX = Math.max(maxX, px);
    minY = Math.min(minY, py);
    maxY = Math.max(maxY, py);
  }
  return { x: minX, y: minY, w: maxX - minX, h: maxY - minY };
}

/** The bounding box an item occupies — a pen stroke's is its path's. */
export function itemBox(item: SketchItem): SketchRect {
  return item.t === 'pen' && item.p?.length ? bboxOfPoints(item.p) : normalisedBox(item);
}

export function rectsOverlap(a: SketchRect, b: SketchRect): boolean {
  return !(a.x > b.x + b.w || a.x + a.w < b.x || a.y > b.y + b.h || a.y + a.h < b.y);
}

export function distToSegment(
  px: number,
  py: number,
  x1: number,
  y1: number,
  x2: number,
  y2: number,
): number {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const len = dx * dx + dy * dy;
  let t = len ? ((px - x1) * dx + (py - y1) * dy) / len : 0;
  t = Math.max(0, Math.min(1, t));
  const ox = x1 + t * dx - px;
  const oy = y1 + t * dy - py;
  return Math.sqrt(ox * ox + oy * oy);
}

/** Text scales with the stroke weight, the same as on the web pad. */
export function textFontSize(item: SketchItem): number {
  return Math.round(10 + item.s * 2.6);
}

export function wrapLines(
  ctx: CanvasRenderingContext2D,
  text: string,
  font: string,
  maxWidth: number,
): string[] {
  ctx.font = font;
  const out: string[] = [];
  for (const paragraph of String(text).split('\n')) {
    const words = paragraph.split(/\s+/);
    let line = '';
    for (const word of words) {
      const next = line ? `${line} ${word}` : word;
      if (line && maxWidth && ctx.measureText(next).width > maxWidth) {
        out.push(line);
        line = word;
      } else {
        line = next;
      }
    }
    out.push(line);
  }
  return out;
}

/** Size a text item to its content — it has no drawn box to bound it. */
export function measureText(ctx: CanvasRenderingContext2D, item: SketchItem): SketchItem {
  const size = textFontSize(item);
  const font = `${size}px ${FONT}`;
  const lines = wrapLines(ctx, item.l, font, 0);
  let width = 0;
  for (const line of lines) width = Math.max(width, ctx.measureText(line).width);
  item.w = Math.max(10, width);
  item.h = Math.max(size, lines.length * size * 1.3);
  return item;
}

/**
 * Paint the paper. Drawn ON the canvas rather than as a CSS backdrop so it is
 * part of the exported PNG — a grid stays a grid on the note card, in ERPat's
 * web module and in the API, not just while you are editing.
 */
export function drawPaper(ctx: CanvasRenderingContext2D, pad: SketchPaper): void {
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, SCENE_W, SCENE_H);
  ctx.lineWidth = 1;

  if (pad === 'lined') {
    ctx.strokeStyle = '#dbe7ea';
    for (let y = 32; y < SCENE_H; y += 32) {
      ctx.beginPath();
      ctx.moveTo(0, y + 0.5);
      ctx.lineTo(SCENE_W, y + 0.5);
      ctx.stroke();
    }
  } else if (pad === 'grid') {
    ctx.strokeStyle = '#e3edef';
    for (let y = 24; y < SCENE_H; y += 24) {
      ctx.beginPath();
      ctx.moveTo(0, y + 0.5);
      ctx.lineTo(SCENE_W, y + 0.5);
      ctx.stroke();
    }
    for (let x = 24; x < SCENE_W; x += 24) {
      ctx.beginPath();
      ctx.moveTo(x + 0.5, 0);
      ctx.lineTo(x + 0.5, SCENE_H);
      ctx.stroke();
    }
  } else if (pad === 'dotted') {
    ctx.fillStyle = '#c3d4d8';
    for (let y = 24; y < SCENE_H; y += 24) {
      for (let x = 24; x < SCENE_W; x += 24) {
        ctx.beginPath();
        ctx.arc(x, y, 1.3, 0, Math.PI * 2);
        ctx.fill();
      }
    }
  }

  ctx.strokeStyle = '#dde6e9';
  ctx.strokeRect(0.5, 0.5, SCENE_W - 1, SCENE_H - 1);
}

function closedPath(ctx: CanvasRenderingContext2D, item: SketchItem): void {
  const { x, y, w, h } = item;
  ctx.beginPath();
  if (item.t === 'decision') {
    ctx.moveTo(x + w / 2, y);
    ctx.lineTo(x + w, y + h / 2);
    ctx.lineTo(x + w / 2, y + h);
    ctx.lineTo(x, y + h / 2);
    ctx.closePath();
    return;
  }
  if (item.t === 'ellipse') {
    ctx.ellipse(x + w / 2, y + h / 2, Math.max(1, w / 2), Math.max(1, h / 2), 0, 0, Math.PI * 2);
    return;
  }
  // process = a lightly rounded box, terminator = a full pill
  const r = Math.max(
    0,
    item.t === 'terminator' ? Math.min(w, h) / 2 : Math.min(6, w / 2, h / 2),
  );
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}

function drawLabel(ctx: CanvasRenderingContext2D, item: SketchItem): void {
  if (!item.l) return;
  const size = 15;
  const font = `600 ${size}px ${FONT}`;
  const lines = wrapLines(ctx, item.l, font, Math.max(20, item.w - 16));
  ctx.font = font;
  ctx.fillStyle = item.c;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  const lineHeight = size * 1.25;
  const top = item.y + item.h / 2 - ((lines.length - 1) * lineHeight) / 2;
  lines.forEach((line, i) => {
    ctx.fillText(line, item.x + item.w / 2, top + i * lineHeight);
  });
  ctx.textAlign = 'start';
  ctx.textBaseline = 'alphabetic';
}

export function drawItem(ctx: CanvasRenderingContext2D, item: SketchItem): void {
  ctx.lineWidth = item.s;
  ctx.strokeStyle = item.c;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';

  if (item.t === 'pen') {
    const points = item.p ?? [];
    const first = points[0];
    if (!first) return;
    if (points.length === 1) {
      // A single tap still leaves a dot rather than nothing.
      ctx.beginPath();
      ctx.arc(first[0], first[1], item.s / 2, 0, Math.PI * 2);
      ctx.fillStyle = item.c;
      ctx.fill();
      return;
    }
    ctx.beginPath();
    ctx.moveTo(first[0], first[1]);
    for (let i = 1; i < points.length; i += 1) {
      const point = points[i];
      if (point) ctx.lineTo(point[0], point[1]);
    }
    ctx.stroke();
    return;
  }

  if (isSegment(item.t)) {
    const x2 = item.x + item.w;
    const y2 = item.y + item.h;
    ctx.beginPath();
    ctx.moveTo(item.x, item.y);
    ctx.lineTo(x2, y2);
    ctx.stroke();
    if (item.t === 'arrow') {
      const angle = Math.atan2(y2 - item.y, x2 - item.x);
      const head = 9 + item.s * 1.8;
      ctx.beginPath();
      ctx.moveTo(x2, y2);
      ctx.lineTo(x2 - head * Math.cos(angle - Math.PI / 7), y2 - head * Math.sin(angle - Math.PI / 7));
      ctx.moveTo(x2, y2);
      ctx.lineTo(x2 - head * Math.cos(angle + Math.PI / 7), y2 - head * Math.sin(angle + Math.PI / 7));
      ctx.stroke();
    }
    return;
  }

  if (item.t === 'text') {
    const size = textFontSize(item);
    ctx.font = `${size}px ${FONT}`;
    ctx.fillStyle = item.c;
    ctx.textBaseline = 'top';
    const lines = wrapLines(ctx, item.l, ctx.font, 0);
    lines.forEach((line, i) => {
      ctx.fillText(line, item.x, item.y + i * size * 1.3);
    });
    ctx.textBaseline = 'alphabetic';
    return;
  }

  closedPath(ctx, item);
  if (item.f) {
    ctx.fillStyle = item.f;
    ctx.fill();
  }
  ctx.stroke();
  drawLabel(ctx, item);
}

/** Paper plus every item — the exact pixels that become the note's picture. */
export function drawScene(ctx: CanvasRenderingContext2D, scene: SketchScene): void {
  drawPaper(ctx, scene.pad);
  for (const item of scene.items) drawItem(ctx, item);
}

/**
 * Topmost item under a point, or -1. Iterates back to front so the item drawn
 * last — the one visually on top — is the one picked.
 *
 * `slack` widens the tolerance for thin shapes; the pad passes a larger value
 * than the web does because a fingertip is not a mouse pointer.
 */
export function hitTest(scene: SketchScene, px: number, py: number, slack = 8): number {
  for (let i = scene.items.length - 1; i >= 0; i -= 1) {
    const item = scene.items[i];
    if (!item) continue;

    if (item.t === 'pen') {
      const points = item.p ?? [];
      for (let k = 1; k < points.length; k += 1) {
        const a = points[k - 1];
        const b = points[k];
        if (!a || !b) continue;
        if (distToSegment(px, py, a[0], a[1], b[0], b[1]) <= item.s + slack) return i;
      }
      continue;
    }

    if (isSegment(item.t)) {
      if (
        distToSegment(px, py, item.x, item.y, item.x + item.w, item.y + item.h) <=
        item.s + slack
      ) {
        return i;
      }
      continue;
    }

    const box = itemBox(item);
    if (
      px >= box.x - slack &&
      px <= box.x + box.w + slack &&
      py >= box.y - slack &&
      py <= box.y + box.h + slack
    ) {
      return i;
    }
  }
  return -1;
}

/**
 * Where a lone selected item's drag handles sit. Closed shapes resize from one
 * corner; segments are re-pointed from either end; pen and text have none.
 */
export function handlePoints(
  item: SketchItem,
): Array<{ x: number; y: number; mode: 'resize' | 'p1' | 'p2' }> {
  if (isSegment(item.t)) {
    return [
      { x: item.x, y: item.y, mode: 'p1' },
      { x: item.x + item.w, y: item.y + item.h, mode: 'p2' },
    ];
  }
  if (isClosed(item.t)) {
    return [{ x: item.x + item.w, y: item.y + item.h, mode: 'resize' }];
  }
  return [];
}

/** Smallest box containing every given item, or null when there are none. */
export function boundsOf(items: ReadonlyArray<SketchItem>): SketchRect | null {
  let box: SketchRect | null = null;
  for (const item of items) {
    const b = itemBox(item);
    if (!box) {
      box = { ...b };
      continue;
    }
    const x = Math.min(box.x, b.x);
    const y = Math.min(box.y, b.y);
    box = {
      x,
      y,
      w: Math.max(box.x + box.w, b.x + b.w) - x,
      h: Math.max(box.y + box.h, b.y + b.h) - y,
    };
  }
  return box;
}

/** Move an item by a delta, carrying a pen stroke's whole path with it. */
export function translateItem(item: SketchItem, from: SketchItem, dx: number, dy: number): void {
  item.x = from.x + dx;
  item.y = from.y + dy;
  if (item.t === 'pen' && item.p && from.p) {
    for (let i = 0; i < item.p.length; i += 1) {
      const target = item.p[i];
      const origin = from.p[i];
      if (target && origin) {
        target[0] = origin[0] + dx;
        target[1] = origin[1] + dy;
      }
    }
  }
}

export function cloneItem(item: SketchItem): SketchItem {
  return { ...item, ...(item.p ? { p: item.p.map(([x, y]) => [x, y] as [number, number]) } : {}) };
}

export function cloneScene(scene: SketchScene): SketchScene {
  return { ...scene, items: scene.items.map(cloneItem) };
}

/** The server's ceilings, from `modules/Notes/libraries/Scribble_scene.php`. */
const MAX_ITEMS = 400;
const MAX_POINTS = 2000;
const MAX_LABEL_CHARS = 240;
const MAX_WEIGHT = 12;
/** Beyond this the server refuses the scene outright rather than truncating. */
export const MAX_SCENE_JSON_BYTES = 2_000_000;

/**
 * Accept a scene from storage or from ERPat, discarding anything malformed.
 *
 * This is a port of the server's `Scribble_scene::sanitize()`, rule for rule —
 * the same 400-item ceiling, 2000-point paths, 12-max stroke, 240-char labels,
 * `#rrggbb`-only colours, coordinates clamped to +/-10000 and ROUNDED TO 2
 * DECIMALS. The rounding is the part that is easy to skip and costly to skip:
 * without it the scene the pad sends and the scene the API sends back differ in
 * the sixth decimal of every pen point, and nothing that compares them (a dirty
 * check, a test, a cache) can ever call them equal.
 *
 * Run on the way OUT as well as in, so what Syntry stores is byte-for-byte what
 * it will read back.
 */
export function parseScene(raw: unknown): SketchScene | null {
  const source = typeof raw === 'string' ? safeJson(raw) : raw;
  if (!source || typeof source !== 'object') return null;
  const record = source as Record<string, unknown>;
  const rawItems = Array.isArray(record.items) ? record.items : null;
  if (!rawItems) return null;

  const pad = SKETCH_PAPERS.some((p) => p.value === record.pad)
    ? (record.pad as SketchPaper)
    : 'blank';

  const shapes: SketchShape[] = [
    'process',
    'decision',
    'terminator',
    'ellipse',
    'line',
    'arrow',
    'pen',
    'text',
  ];
  const items: SketchItem[] = [];
  for (const entry of rawItems) {
    if (items.length >= MAX_ITEMS) break;
    if (!entry || typeof entry !== 'object') continue;
    const it = entry as Record<string, unknown>;
    const t = shapes.find((shape) => shape === it.t);
    if (!t) continue;
    const item: SketchItem = {
      t,
      x: num(it.x),
      y: num(it.y),
      w: num(it.w),
      h: num(it.h),
      c: typeof it.c === 'string' && /^#[0-9a-f]{6}$/i.test(it.c) ? it.c : '#1f2933',
      f: typeof it.f === 'string' && /^#[0-9a-f]{6}$/i.test(it.f) ? it.f : '',
      // `(int) min(12, max(1, num))` on the server: a MISSING weight becomes 1,
      // not the pad's default, and 4.7 TRUNCATES to 4 rather than rounding to 5.
      s: Math.trunc(Math.min(MAX_WEIGHT, Math.max(1, num(it.s)))),
      l: typeof it.l === 'string' ? it.l.trim().slice(0, MAX_LABEL_CHARS) : '',
    };
    if (t === 'pen') {
      const path = Array.isArray(it.p) ? it.p : [];
      const points: Array<[number, number]> = [];
      for (const point of path) {
        if (points.length >= MAX_POINTS) break;
        if (!Array.isArray(point) || point.length < 2) continue;
        if (!isNumeric(point[0]) || !isNumeric(point[1])) continue;
        points.push([num(point[0]), num(point[1])]);
      }
      // A one-point stroke draws nothing and only costs storage.
      if (points.length < 2) continue;
      item.p = points;
    }
    // A text item is nothing but its label; an empty one is a box no one can
    // see or select. The server drops these, so the client must too — otherwise
    // a saved scene comes back one item shorter than the one that was sent.
    if (t === 'text' && item.l === '') continue;
    items.push(item);
  }

  return { v: 1, pad, w: SCENE_W, h: SCENE_H, items };
}

function isNumeric(value: unknown): boolean {
  return typeof value === 'number' ? Number.isFinite(value) : Number.isFinite(Number(value));
}

function safeJson(raw: string): unknown {
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

/**
 * One scene number, bounded and rounded exactly as `Scribble_scene::num()` does.
 *
 * The rounding is deliberately NOT `Math.round`. PHP's `round()` goes half AWAY
 * FROM ZERO, JavaScript's goes half UP: on -0.005 that is -0.01 against -0.00.
 * A pen stroke drawn upward is full of negative halves, so the naive version
 * makes a scene differ from the one the server stores in a way that only shows
 * up as a phantom "unsaved changes" much later.
 */
function num(value: unknown): number {
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n)) return 0;
  const bounded = Math.max(-10000, Math.min(10000, n));
  return (Math.sign(bounded) * Math.round(Math.abs(bounded) * 100)) / 100;
}
